import { useEffect, useState } from 'react';
import {
  fetchGAAccounts,
  saveGAProperties,
  type GAAccount,
} from '../../api/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface GAPropertySelectorProps {
  sessionId: string;
  onSaved: (count: number) => void;
  onClose: () => void;
}

/**
 * Single-step GA4 property picker (mirrors mvp's
 * GoogleAnalyticsPropertySelector). Loads accounts + properties and lets the
 * user multi-select properties grouped by account, with a global Select All.
 */
export default function GAPropertySelector({ sessionId, onSaved, onClose }: GAPropertySelectorProps) {
  const [accounts, setAccounts] = useState<GAAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchGAAccounts(sessionId)
      .then((data) => {
        if (cancelled) return;
        setAccounts(data.accounts || []);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load properties. Please try again.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [sessionId]);

  const allProperties = accounts.flatMap((a) =>
    a.properties.map((p) => ({ ...p, accountName: a.account_name })),
  );

  const toggleProperty = (propertyId: string) => {
    setSelectedIds((prev) =>
      prev.includes(propertyId) ? prev.filter((id) => id !== propertyId) : [...prev, propertyId],
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === allProperties.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allProperties.map((p) => p.property_id));
    }
  };

  const handleConfirm = async () => {
    if (selectedIds.length === 0) return;
    setSaving(true);
    try {
      const result = await saveGAProperties({
        session_id: sessionId,
        properties: allProperties
          .filter((p) => selectedIds.includes(p.property_id))
          .map((p) => ({
            property_id: p.property_id,
            property_name: p.property_name,
            account_name: p.accountName,
          })),
      });
      onSaved(result.count);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save properties');
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <img
              src="/logos/google_analytics_logo.svg"
              alt="Google Analytics"
              className="size-8 shrink-0 object-contain"
            />
            <div>
              <DialogTitle>Select GA4 Properties</DialogTitle>
              <DialogDescription>Choose the properties to analyze</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto pr-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
              <Loader2 className="size-8 animate-spin text-foreground" />
              <p className="text-sm">Loading Google Analytics properties...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col gap-2 py-8 text-center">
              <p className="text-sm text-destructive">{error}</p>
              <button
                type="button"
                onClick={onClose}
                className="text-sm font-medium text-foreground hover:text-muted-foreground"
              >
                Close
              </button>
            </div>
          ) : allProperties.length === 0 ? (
            <p className="rounded-lg bg-muted/50 py-4 text-center text-sm text-muted-foreground">
              No Google Analytics properties found for this account
            </p>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold">
                  {allProperties.length} {allProperties.length === 1 ? 'property' : 'properties'}
                </h3>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-xs font-medium text-foreground hover:text-muted-foreground"
                >
                  {selectedIds.length === allProperties.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div className="flex flex-col gap-4">
                {accounts
                  .filter((a) => a.properties.length > 0)
                  .map((account) => (
                    <div key={account.account_id}>
                      <p className="mb-1.5 text-sm text-muted-foreground">{account.account_name}</p>
                      <div className="flex flex-col gap-2">
                        {account.properties.map((property) => {
                          const isSelected = selectedIds.includes(property.property_id);
                          return (
                            <label
                              key={property.property_id}
                              className={cn(
                                'flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors',
                                isSelected
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:bg-muted/50',
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleProperty(property.property_id)}
                                className="size-4 shrink-0 accent-primary"
                              />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium">{property.property_name}</p>
                                <p className="truncate text-xs text-muted-foreground">
                                  ID: {property.property_id}
                                </p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>

        {!loading && !error && (
          <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
            <p className="text-sm text-muted-foreground">{selectedIds.length} selected</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={saving || selectedIds.length === 0}>
                {saving ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Confirm'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
